import React from 'react';
import { useNavigate } from 'react-router-dom';
import { User, MapPin, Bell, HelpCircle, FileText, LogOut, ChevronRight, Shield, Star, Wallet, Users, Briefcase, RefreshCw } from 'lucide-react';
import { useApp } from '../context/AppContext';
import BottomNav from '../components/BottomNav';
import DesktopHeader from '../components/DesktopHeader';

const Profile = () => {
  const nav = useNavigate();
  const { user, logout, role, setRole, bookings, resetAll } = useApp();

  const handleLogout = () => { logout(); nav('/'); };

  const handleResetCatalog = () => {
    if (window.confirm('This will clear cached data and reload the latest catalog (Sofa Cleaning, Hourly tags, etc). Continue?')) {
      localStorage.removeItem('c24_categories');
      localStorage.removeItem('c24_services');
      localStorage.removeItem('c24_offers');
      localStorage.removeItem('c24_data_version');
      window.location.href = '/home';
    }
  };

  const menu = [
    { icon: MapPin, label: 'Saved addresses', to: '#' },
    { icon: Wallet, label: 'Wallet & Payments', to: '/wallet', testid: 'profile-menu-wallet' },
    { icon: Star, label: 'Rewards', to: '/rewards', testid: 'profile-menu-rewards' },
    { icon: Users, label: 'Refer & Earn', to: '/referral', testid: 'profile-menu-referral' },
    { icon: Bell, label: 'Notifications', to: '#' },
    { icon: FileText, label: 'Terms & Privacy', to: '/terms' },
    { icon: HelpCircle, label: 'Help & Support', to: '/support' },
  ];

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 md:pb-8">
      <DesktopHeader />

      <div className="md:hidden px-5 pt-6 pb-4 bg-gradient-to-b from-[#FFF7C2] to-white">
        <h1 className="text-2xl font-extrabold">Profile</h1>
      </div>

      <div className="max-w-[1100px] mx-auto px-5 md:px-6 pt-4 md:pt-8">
        <h1 className="hidden md:block text-2xl font-extrabold mb-5">My Profile</h1>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-4 md:col-span-1">
            <div className="bg-white border border-neutral-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                <User className="w-7 h-7 text-[#2E7D32]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-neutral-900 truncate">{user?.name || 'Guest'}</p>
                <p className="text-xs text-neutral-500 truncate">{user?.email || 'Not signed in'}</p>
              </div>
              <button className="text-[#2E7D32] text-xs font-semibold border border-[#2E7D32] px-3 py-1 rounded-full hover:bg-[#E8F5E9] transition-colors">Edit</button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <StatCard icon={<Briefcase className="w-4 h-4 text-[#2E7D32]" />} label="Bookings" value={bookings.length} />
              <StatCard icon={<Star className="w-4 h-4 text-[#F5B301]" />} label="Rewards" value="250" />
              <StatCard icon={<Wallet className="w-4 h-4 text-[#EA580C]" />} label="Wallet" value="₹0" />
            </div>

            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <p className="text-xs text-neutral-500 mb-2 font-semibold">SWITCH ROLE</p>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => { setRole('customer'); nav('/home'); }}
                  className={`py-2.5 rounded-lg text-sm font-semibold transition-colors ${role === 'customer' ? 'bg-[#2E7D32] text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}>Customer</button>
                <button onClick={() => { setRole('cleaner'); nav('/cleaner/dashboard'); }}
                  className={`py-2.5 rounded-lg text-sm font-semibold transition-colors ${role === 'cleaner' ? 'bg-[#2E7D32] text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}>Cleaner</button>
              </div>
            </div>
          </div>

          <div className="md:col-span-2 space-y-2">
            {menu.map((m, i) => (
              <button key={i} data-testid={m.testid} onClick={() => m.to !== '#' && nav(m.to)} className="w-full flex items-center gap-3 bg-white border border-neutral-200 rounded-xl p-4 hover:bg-neutral-50 transition-colors">
                <div className="w-9 h-9 rounded-full bg-[#F5F5F5] flex items-center justify-center">
                  <m.icon className="w-4 h-4 text-neutral-700" />
                </div>
                <span className="flex-1 text-left text-sm font-medium">{m.label}</span>
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              </button>
            ))}

            <button onClick={handleResetCatalog} className="w-full flex items-center gap-3 bg-white border border-blue-100 rounded-xl p-4 text-blue-700 hover:bg-blue-50 transition-colors">
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center">
                <RefreshCw className="w-4 h-4" />
              </div>
              <div className="flex-1 text-left">
                <span className="block text-sm font-semibold">Refresh catalog</span>
                <span className="block text-[11px] text-blue-600/80">Reload latest services (Sofa, Hourly tags, etc.)</span>
              </div>
              <ChevronRight className="w-4 h-4" />
            </button>

            <button onClick={handleLogout} className="w-full flex items-center gap-3 bg-white border border-red-100 rounded-xl p-4 text-red-600 hover:bg-red-50 transition-colors">
              <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center">
                <LogOut className="w-4 h-4" />
              </div>
              <span className="flex-1 text-left text-sm font-semibold">Log out</span>
            </button>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-3 text-center">
    <div className="w-8 h-8 rounded-full bg-neutral-50 flex items-center justify-center mx-auto">{icon}</div>
    <p className="mt-1 text-[11px] text-neutral-500">{label}</p>
    <p className="font-bold text-sm">{value}</p>
  </div>
);

export default Profile;
